/**
 * ComposeFab — floating "贴一张" button.
 *
 * H5 source: `.compose-button` (styles.css:2304–2383), wired in index.html as
 * <button id="composeButton" class="compose-button">.
 *
 * In H5 the button lives outside every window-view but inside `<main#app>`;
 * we keep the same semantic by mounting ComposeFab inside <AppShell> and
 * letting App.tsx render it once. It's purely presentational here — the
 * caller decides what `onTrigger` does (gate auth, open compose store, etc.).
 */

import type { ReactNode } from "react";

export interface ComposeFabProps {
  onTrigger: () => void;
  /**
   * Override label. H5 真源 uses "发布" (see `index.html` `#composeButton` +
   * main.js, validated 2026-05-18 via Playwright `getComputedStyle` against
   * :8090). Keep prop overridable so callers can opt into the older "贴一张"
   * / "贴纸条" experiments if needed.
   */
  label?: string;
  /** Override the leading icon. Defaults to the H5 plus glyph. */
  icon?: ReactNode;
  /** Hide the FAB without unmounting (mirrors H5 `[hidden]`). */
  hidden?: boolean;
}

export function ComposeFab({
  onTrigger,
  label = "发布",
  icon,
  hidden = false,
}: ComposeFabProps) {
  return (
    <button
      type="button"
      className="compose-button"
      hidden={hidden || undefined}
      aria-label={label}
      aria-haspopup="dialog"
      onClick={onTrigger}
    >
      {icon ?? <span className="compose-icon" aria-hidden="true" />}
      <span>{label}</span>
    </button>
  );
}
